import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Container, Row, Col, Form, Spinner, Alert } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaFire,
  FaTrashAlt,
  FaChartLine,
  FaExchangeAlt,
  FaCoins,
  FaShieldAlt,
  FaUserTie,
  FaUmbrellaBeach,
  FaExclamationTriangle,
  FaCheckCircle,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaFileInvoiceDollar,
  FaBalanceScale,
  FaTrophy,
  FaHandHoldingUsd,
  FaLayerGroup
} from "react-icons/fa";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";
import "../css/WaFire.css";

// Chart.js 컴포넌트 등록
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// 선택 가능한 미국 고배당/인컴 및 성장 ETF 메타데이터
const ETF_DATABASE = {
  JEPQ: { name: "JPMorgan Nasdaq Equity Premium Income", tag: "나스닥 커버드콜", yieldEst: "9~11%", desc: "나스닥100 기반 고배당 월배당의 대명사" },
  SPYI: { name: "NEOS S&P 500 High Income", tag: "S&P500 옵션 인컴", yieldEst: "11~12%", desc: "S&P500 기반 세금 효율적 월배당" },
  JEPI: { name: "JPMorgan Equity Premium Income", tag: "S&P500 저변동 배당", yieldEst: "7~8%", desc: "낮은 변동성과 안정적 방어형 인컴" },
  GPIQ: { name: "Goldman Sachs Nasdaq-100 Core Premium", tag: "골드만 나스닥 커버드콜", yieldEst: "9~11%", desc: "골드만삭스의 나스닥 월배당 ETF" },
  GPIX: { name: "Goldman Sachs S&P 500 Core Premium", tag: "골드만 S&P500 커버드콜", yieldEst: "8~9%", desc: "골드만삭스의 S&P500 월배당 ETF" },
  QQQI: { name: "NEOS Nasdaq-100 High Income", tag: "나스닥 옵션 인컴", yieldEst: "13~14%", desc: "NEOS의 나스닥 초고배당 월배당" },
  SCHD: { name: "Schwab U.S. Dividend Equity", tag: "미국 정통 배당성장", yieldEst: "3.5~4%", desc: "국민 배당성장주 (분기배당이지만 배당성장의 왕)" },
  DIVO: { name: "Amplify CWP Enhanced Dividend", tag: "우량 배당주 + 전술 옵션", yieldEst: "4.5~5.5%", desc: "배당성장과 선별적 콜옵션의 조화" },
  TLTW: { name: "iShares 20+ Year Treasury Bond BuyWrite", tag: "미국 20년 국채 커버드콜", yieldEst: "15~18%", desc: "초장기 미국채 기반 초고배당 인컴" },
  SGOV: { name: "iShares 0-3 Month Treasury Bond", tag: "미국 초단기 국채 (파킹)", yieldEst: "4.5~5.2%", desc: "무위험 달러 파킹통장 성격의 안전자산" },
  QLD: { name: "ProShares Ultra QQQ", tag: "나스닥 2배 레버리지", yieldEst: "0.3~0.5%", desc: "나스닥100 지수의 일간 수익률을 2배 추종하는 대표 성장 레버리지 ETF" },
  QQQM: { name: "Invesco NASDAQ 100 ETF", tag: "나스닥100 저보수 성장", yieldEst: "0.5~0.7%", desc: "QQQ 대비 보수가 저렴해 장기 적립 및 복리 투자에 최적화된 나스닥100 ETF" }
};

const AVAILABLE_SYMBOLS = Object.keys(ETF_DATABASE);

// 금액 한글 포맷 유틸 (예: 3억원, 3억 257만원, 2,429만원)
const formatKoreanAmount = (num) => {
  if (!num || isNaN(num)) return "0원";
  const abs = Math.abs(num);
  const uk = Math.floor(abs / 100000000);
  const man = Math.floor((abs % 100000000) / 10000);
  const won = Math.floor(abs % 10000);

  let result = [];
  if (uk > 0) result.push(`${uk}억`);
  if (man > 0) result.push(`${man.toLocaleString()}만`);
  if (won > 0 && uk === 0 && man === 0) result.push(`${won.toLocaleString()}`);

  const str = result.join(" ") + "원";
  return num < 0 ? `-${str}` : str;
};

// 원화 단위 콤마 포맷
const formatKRW = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "0";
  return Math.round(num).toLocaleString("ko-KR");
};

// 달러 단위 포맷
const formatUSD = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "0.00";
  return Number(num).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// 위트 있는 개구리 판정 코멘트
const getFrogVerdict = (monthlyNet) => {
  if (monthlyNet < 1000000) {
    return { title: "🐸 컵라면 & 삼각김밥 생존 파이어", desc: "기본 생활비 절약 필수! 아직은 허리띠를 졸라매야 해요." };
  } else if (monthlyNet < 2000000) {
    return { title: "🐸 1인 가구 자립 파이어", desc: "숨만 쉬어도 월세와 기본 식비 해결! 소소한 자유 획득!" };
  } else if (monthlyNet < 3500000) {
    return { title: "🐸 회사 탈출 정식 승인 (중산층 파이어)", desc: "직장 상사 얼굴 안 봐도 되는 진정한 경제적 자유 달성!" };
  } else {
    return { title: "👑 갓물주 부럽지 않은 황제 파이어!", desc: "매달 통장에 꽂히는 여유로운 배당 라이프 만끽!" };
  }
};

// 월의 시작일(01일) 구하기 (YYYY-MM-01)
const getMonthStart = (yyyyMm) => {
  if (!yyyyMm) return "";
  return `${yyyyMm}-01`;
};

// 월의 마지막 날 구하기 (YYYY-MM-[말일])
// 예: 2025-12 -> 2025-12-31, 2024-02 -> 2024-02-29, 2023-04 -> 2023-04-30
const getMonthEnd = (yyyyMm) => {
  if (!yyyyMm || !/^\d{4}-\d{2}$/.test(yyyyMm)) return yyyyMm;
  const [year, month] = yyyyMm.split("-").map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  return `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
};

// 커스텀 월(YYYY-MM) 인풋 컴포넌트:
// 사용자는 '연-월(YYYY-MM)'만 선택하며, '일(Day)'은 노출되지 않습니다.
// 실제 백엔드 요청 시에는 시작일=01일, 종료일=말일로 자동 변환되어 전송됩니다.
const WaFireMonthInput = ({ label, value, onChange, min, max }) => {
  const hiddenMonthRef = useRef(null);
  const [text, setText] = useState(value || "");

  useEffect(() => {
    setText(value || "");
  }, [value]);

  const handleOpenPicker = () => {
    if (hiddenMonthRef.current) {
      if (typeof hiddenMonthRef.current.showPicker === "function") {
        try {
          hiddenMonthRef.current.showPicker();
          return;
        } catch (e) {
          // fallback to focus/click
        }
      }
      hiddenMonthRef.current.click();
    }
  };

  const handleTextChange = (e) => {
    let raw = e.target.value.replace(/[^0-9-]/g, "");
    if (/^\d{6}$/.test(raw)) {
      raw = `${raw.slice(0, 4)}-${raw.slice(4, 6)}`;
    }
    setText(raw);
    if (/^\d{4}-\d{2}$/.test(raw)) {
      let clamped = raw;
      if (min && clamped < min) clamped = min;
      if (max && clamped > max) clamped = max;
      onChange(clamped);
    }
  };

  const handleBlur = () => {
    let val = text.trim();
    if (/^\d{4}-\d{2}$/.test(val)) {
      if (min && val < min) val = min;
      if (max && val > max) val = max;
      setText(val);
      onChange(val);
    } else {
      setText(value || "");
    }
  };

  return (
    <div>
      {label && <Form.Label className="text-muted" style={{ fontSize: "0.75rem" }}>{label}</Form.Label>}
      <div className="wafire-date-input-wrap position-relative">
        <Form.Control
          type="text"
          value={text}
          onChange={handleTextChange}
          onBlur={handleBlur}
          placeholder="YYYY-MM"
          maxLength={7}
          className="rounded-3 wafire-date-text-input"
          style={{
            paddingRight: "42px",
            fontSize: "0.95rem",
            fontWeight: "500",
            letterSpacing: "0.02em"
          }}
        />
        <button
          type="button"
          className="wafire-date-picker-trigger position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-secondary p-1 d-flex align-items-center justify-content-center"
          onClick={handleOpenPicker}
          title="달력에서 연-월 선택"
          style={{ cursor: "pointer", zIndex: 3 }}
        >
          <FaCalendarAlt size={16} />
        </button>

        {/* 숨겨진 네이티브 month input: 브라우저 네이티브 연-월 선택 팝업 제공 */}
        <input
          ref={hiddenMonthRef}
          type="month"
          min={min}
          max={max}
          value={value || ""}
          onChange={(e) => {
            if (e.target.value) {
              setText(e.target.value);
              onChange(e.target.value);
            }
          }}
          tabIndex={-1}
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "40px",
            height: "100%",
            opacity: 0,
            cursor: "pointer",
            pointerEvents: "auto",
            zIndex: 2
          }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
};


const WaFireSimulator = () => {
  // DB 데이터 보관 정책: 오늘 기준 최대 과거 5년 전까지만 데이터 존재 (연-월 기준)
  const { minMonth, maxMonth } = useMemo(() => {
    const today = new Date();
    const currentY = today.getFullYear();
    const currentM = String(today.getMonth() + 1).padStart(2, "0");
    const maxM = `${currentY}-${currentM}`;

    const fiveYearsAgo = new Date();
    fiveYearsAgo.setFullYear(today.getFullYear() - 5);
    const minY = fiveYearsAgo.getFullYear();
    const minM = String(fiveYearsAgo.getMonth() + 1).padStart(2, "0");
    const minMVal = `${minY}-${minM}`;

    return {
      minMonth: minMVal,
      maxMonth: maxM
    };
  }, []);

  // 1. 입력 상태 관리
  // 투자 방식: "COMPARE"(맞대결 비교, 기본값) | "LUMP_SUM"(거치식) | "DCA"(적립식)
  const [investmentType, setInvestmentType] = useState("COMPARE");
  const [totalCapital, setTotalCapital] = useState(300000000); // 거치식 자본금 (기본 3억 원)
  const [monthlyDeposit, setMonthlyDeposit] = useState(2500000); // 적립식 월 납입액 (기본 250만 원)

  // 백테스트 시작 월 기본값 (2023-01, 5년 범위 내로 보정)
  const [startMonth, setStartMonth] = useState(() => {
    const today = new Date();
    const fiveYearsAgo = new Date();
    fiveYearsAgo.setFullYear(today.getFullYear() - 5);
    const minY = fiveYearsAgo.getFullYear();
    const minM = String(fiveYearsAgo.getMonth() + 1).padStart(2, "0");
    const minMVal = `${minY}-${minM}`;
    return "2023-01" < minMVal ? minMVal : "2023-01";
  });

  // 백테스트 종료 월 기본값 (이번 달)
  const [endMonth, setEndMonth] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  });

  const [status, setStatus] = useState("UNEMPLOYED"); // "UNEMPLOYED" | "EMPLOYED"
  const [otherAnnualIncome, setOtherAnnualIncome] = useState(0);

  // 맞대결 모드 결과 내 상세 뷰 탭: "summary"(종합 맞대결) | "lumpSum"(거치식 상세) | "dca"(적립식 상세)
  const [compareSubTab, setCompareSubTab] = useState("summary");

  // 월별 차트 및 표 세전/세후 표기 모드: "net"(세후 실수령, 기본값) | "gross"(세전)
  const [chartTaxMode, setChartTaxMode] = useState("net");

  // ETF 포트폴리오 비중 (기본: JEPQ 50%, SPYI 50%)
  const [allocations, setAllocations] = useState([
    { symbol: "JEPQ", ratio: 0.5 },
    { symbol: "SPYI", ratio: 0.5 }
  ]);

  // 2. API 실행 상태 및 응답 데이터
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [result, setResult] = useState(null);

  // 비중 총합 계산
  const totalRatio = useMemo(() => {
    return allocations.reduce((acc, cur) => acc + (Number(cur.ratio) || 0), 0);
  }, [allocations]);

  const totalPercent = useMemo(() => {
    return Math.round(totalRatio * 100);
  }, [totalRatio]);

  const isRatioValid = Math.abs(totalRatio - 1.0) < 0.001;

  // 빠른 자본금 증액 핸들러
  const handleAddCapital = (amount) => {
    setTotalCapital((prev) => Math.max(0, prev + amount));
  };

  // 빠른 적립액 증액 핸들러
  const handleAddMonthlyDeposit = (amount) => {
    setMonthlyDeposit((prev) => Math.max(0, prev + amount));
  };

  // 시작 월 변경 핸들러 (최대 과거 5년 ~ 이번 달 범위로 강제)
  const handleStartMonthChange = (val) => {
    if (!val || !/^\d{4}-\d{2}$/.test(val)) {
      setStartMonth(val);
      return;
    }
    if (val < minMonth) {
      setStartMonth(minMonth);
    } else if (val > maxMonth) {
      setStartMonth(maxMonth);
    } else {
      setStartMonth(val);
    }
  };

  // 종료 월 변경 핸들러
  const handleEndMonthChange = (val) => {
    if (!val || !/^\d{4}-\d{2}$/.test(val)) {
      setEndMonth(val);
      return;
    }
    if (val < startMonth) {
      setEndMonth(startMonth);
    } else if (val > maxMonth) {
      setEndMonth(maxMonth);
    } else {
      setEndMonth(val);
    }
  };

  // 적립식 총 투자 기간(개월 수) 및 총 예상 누적 투자 원금 계산
  const { dcaMonths, dcaPeriodText, totalDcaCapital } = useMemo(() => {
    if (!startMonth || !endMonth) return { dcaMonths: 0, dcaPeriodText: "", totalDcaCapital: 0 };
    const [startY, startM] = startMonth.split("-").map(Number);
    const [endY, endM] = endMonth.split("-").map(Number);
    if (isNaN(startY) || isNaN(startM) || isNaN(endY) || isNaN(endM)) {
      return { dcaMonths: 0, dcaPeriodText: "", totalDcaCapital: 0 };
    }

    const months = Math.max(1, (endY - startY) * 12 + (endM - startM) + 1);
    const years = Math.floor(months / 12);
    const remMonths = months % 12;

    let text = "";
    if (years > 0 && remMonths > 0) {
      text = `${years}년 ${remMonths}개월 (${months}회차 납입)`;
    } else if (years > 0) {
      text = `${years}년 (${months}회차 납입)`;
    } else {
      text = `${months}개월 (${months}회차 납입)`;
    }

    const totalCap = Number(monthlyDeposit || 0) * months;
    return {
      dcaMonths: months,
      dcaPeriodText: text,
      totalDcaCapital: totalCap
    };
  }, [startMonth, endMonth, monthlyDeposit]);

  // ETF 종목 추가
  const handleAddSymbol = (symbol) => {
    if (allocations.some((a) => a.symbol === symbol)) return;
    const newAllocations = [...allocations, { symbol, ratio: 0 }];
    setAllocations(newAllocations);
  };

  // ETF 종목 삭제
  const handleRemoveSymbol = (idx) => {
    if (allocations.length <= 1) return;
    const next = allocations.filter((_, i) => i !== idx);
    setAllocations(next);
  };

  // 비중 슬라이더/인풋 변경
  const handleRatioChange = (idx, newPercent) => {
    const val = Math.max(0, Math.min(100, Number(newPercent) || 0));
    const next = [...allocations];
    next[idx] = { ...next[idx], ratio: val / 100 };
    setAllocations(next);
  };

  // 1/N 균등 배분
  const handleEqualWeight = () => {
    const n = allocations.length;
    if (n === 0) return;
    const equalRatio = 1.0 / n;
    setAllocations(allocations.map((a) => ({ ...a, ratio: equalRatio })));
  };

  // 100% 자동 맞춤
  const handleAutoAdjust = () => {
    if (totalRatio === 0) {
      handleEqualWeight();
      return;
    }
    const factor = 1.0 / totalRatio;
    setAllocations(allocations.map((a) => ({ ...a, ratio: a.ratio * factor })));
  };

  // API 호출 핸들러 (특정 모드 지정 가능)
  const fetchSimulation = async (targetMode) => {
    // 이벤트 객체가 넘어오는 경우 대비: 문자열 모드명만 허용
    const currentMode = (typeof targetMode === "string" ? targetMode : null) || investmentType;

    if (!isRatioValid) {
      setErrorMsg("비중의 합이 100%여야 합니다.");
      return;
    }

    if (startMonth < minMonth || startMonth > maxMonth) {
      setErrorMsg(`백테스트 시작 월은 ${minMonth}부터 ${maxMonth} 사이(최근 5년 이내)여야 합니다.`);
      return;
    }

    if (endMonth < startMonth) {
      setErrorMsg("종료 월은 시작 월 이후여야 합니다.");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    // 백엔드 API 명세 준수:
    // 시작 일자는 해당 월의 01일로 고정 (YYYY-MM-01)
    // 종료 일자는 해당 월의 마지막 날로 고정 (YYYY-MM-말일, 예: 12월 31일)
    const reqStartDate = getMonthStart(startMonth);
    const reqEndDate = getMonthEnd(endMonth);

    const payload = {
      investmentType: currentMode,
      totalCapital: Number(totalCapital),
      ...(currentMode !== "LUMP_SUM" ? { monthlyDeposit: Number(monthlyDeposit) } : {}),
      startDate: reqStartDate,
      endDate: reqEndDate,
      allocations: allocations.map((a) => ({
        symbol: a.symbol,
        ratio: Number(a.ratio.toFixed(4))
      })),
      userProfile: {
        status,
        otherAnnualIncome: Number(otherAnnualIncome) || 0
      }
    };

    try {
      let response;
      try {
        // 1차 시도: 공식 직통 엔드포인트
        response = await axios.post("https://n8n.wajok.com/webhook/api/wafire", payload, {
          headers: { "Content-Type": "application/json" },
          timeout: 45000
        });
      } catch (directErr) {
        // 브라우저 CORS 제한 시 내장 프록시로 폴백
        console.warn("Direct API call failed, falling back to /api/wafire proxy:", directErr.message);
        response = await axios.post("/api/wafire", payload, {
          headers: { "Content-Type": "application/json" },
          timeout: 45000
        });
      }

      if (response && response.data) {
        setResult((prev) => {
          if (currentMode === "COMPARE") {
            return {
              ...response.data,
              investmentType: "COMPARE"
            };
          } else if (currentMode === "DCA") {
            return {
              ...(prev || {}),
              investmentType: "DCA",
              dca: response.data,
              summary: response.data.summary,
              holdings: response.data.holdings,
              yearlySummary: response.data.yearlySummary,
              monthlyHistory: response.data.monthlyHistory
            };
          } else {
            return {
              ...(prev || {}),
              investmentType: "LUMP_SUM",
              lumpSum: response.data,
              summary: response.data.summary,
              holdings: response.data.holdings,
              yearlySummary: response.data.yearlySummary,
              monthlyHistory: response.data.monthlyHistory
            };
          }
        });
      } else {
        throw new Error("서버에서 올바른 시뮬레이션 데이터를 반환하지 않았습니다.");
      }
    } catch (err) {
      console.error("WaFire simulation fetch error:", err);
      setErrorMsg(
        err.response?.data?.message ||
        err.message ||
        "시뮬레이션 연산 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요."
      );
    } finally {
      setLoading(false);
    }
  };

  // 상단 투자 모드 탭 전환 핸들러 (탭 이동 시 즉시 하단 대시보드 자동 동기화)
  const handleModeSwitch = (targetMode) => {
    if (investmentType === targetMode) return;
    setInvestmentType(targetMode);

    // 해당 모드에 필요한 데이터가 현재 메모리에 존재하는지 확인
    const hasData = (
      (targetMode === "COMPARE" && result && result.comparison && result.lumpSum && result.dca) ||
      (targetMode === "LUMP_SUM" && result && (result.lumpSum || result.investmentType === "LUMP_SUM")) ||
      (targetMode === "DCA" && result && (result.dca || result.investmentType === "DCA"))
    );

    // 데이터가 아직 없다면 즉시 백엔드 시뮬레이션 자동 호출하여 동기화
    if (!hasData) {
      fetchSimulation(targetMode);
    }
  };

  // 마운트 시 기본 조건으로 자동 시뮬레이션 1회 실행
  useEffect(() => {
    fetchSimulation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 미선택 종목 목록 (드롭다운 추가용)
  const unselectedSymbols = useMemo(() => {
    const selected = new Set(allocations.map((a) => a.symbol));
    return AVAILABLE_SYMBOLS.filter((s) => !selected.has(s));
  }, [allocations]);

  // =========================================================================
  // 차트 데이터 가공 (Chart.js)
  // =========================================================================

  // 1. COMPARE 모드: 월별 배당금 맞대결 차트 (Grouped Bar Chart)
  const compareMonthlyChartData = useMemo(() => {
    if (!result || !result.lumpSum || !result.dca) return null;
    const lumpHist = result.lumpSum.monthlyHistory || {};
    const dcaHist = result.dca.monthlyHistory || {};
    const lumpYearly = result.lumpSum.yearlySummary || {};
    const dcaYearly = result.dca.yearlySummary || {};
    const months = Array.from(new Set([...Object.keys(lumpHist), ...Object.keys(dcaHist)])).sort();
    const isNet = chartTaxMode === "net";

    const getNetRatio = (yearlySummary, m) => {
      const year = m.split("-")[0];
      const yData = yearlySummary?.[year];
      if (!yData || !yData.grossKRW || yData.grossKRW <= 0) return 0.85;
      return yData.netKRW / yData.grossKRW;
    };

    return {
      labels: months,
      chartTaxMode,
      datasets: [
        {
          label: isNet ? "거치식 (Lump-sum) 월 실수령액" : "거치식 (Lump-sum) 월 세전 배당금",
          data: months.map((m) => {
            const gross = lumpHist[m]?.grossKRW || 0;
            const ratio = getNetRatio(lumpYearly, m);
            return isNet ? Math.round(gross * ratio) : Math.round(gross);
          }),
          backgroundColor: "rgba(99, 102, 241, 0.8)",
          borderRadius: 4
        },
        {
          label: isNet ? "적립식 (DCA) 월 실수령액" : "적립식 (DCA) 월 세전 배당금",
          data: months.map((m) => {
            const gross = dcaHist[m]?.grossKRW || 0;
            const ratio = getNetRatio(dcaYearly, m);
            return isNet ? Math.round(gross * ratio) : Math.round(gross);
          }),
          backgroundColor: "rgba(16, 185, 129, 0.8)",
          borderRadius: 4
        }
      ]
    };
  }, [result, chartTaxMode]);

  // 2. COMPARE 모드: 연도별 실수령액 맞대결 차트 (Grouped Bar Chart)
  const compareYearlyChartData = useMemo(() => {
    if (!result || !result.lumpSum || !result.dca) return null;
    const lumpYearly = result.lumpSum.yearlySummary || {};
    const dcaYearly = result.dca.yearlySummary || {};
    const years = Array.from(new Set([...Object.keys(lumpYearly), ...Object.keys(dcaYearly)])).sort();

    return {
      labels: years.map((y) => `${y}년`),
      datasets: [
        {
          label: "거치식 연간 실수령액",
          data: years.map((y) => lumpYearly[y]?.netKRW || 0),
          backgroundColor: "#4f46e5",
          borderRadius: 6
        },
        {
          label: "적립식 연간 실수령액",
          data: years.map((y) => dcaYearly[y]?.netKRW || 0),
          backgroundColor: "#10b981",
          borderRadius: 6
        }
      ]
    };
  }, [result]);

  // 3. 단독 모드 (LUMP_SUM 또는 DCA): 현재 활성화된 모드에 맞는 시뮬레이션 데이터 선택
  const activeSingleData = useMemo(() => {
    if (!result) return null;
    if (investmentType === "COMPARE") {
      if (result.investmentType === "COMPARE") {
        return compareSubTab === "dca" ? (result.dca || result) : (result.lumpSum || result);
      }
      return result;
    }
    if (investmentType === "LUMP_SUM") {
      return result.lumpSum || (result.investmentType === "LUMP_SUM" ? result : null);
    }
    if (investmentType === "DCA") {
      return result.dca || (result.investmentType === "DCA" ? result : null);
    }
    return result;
  }, [result, investmentType, compareSubTab]);

  const singleMonthlyChartData = useMemo(() => {
    if (!activeSingleData || !activeSingleData.monthlyHistory) return null;
    const history = activeSingleData.monthlyHistory;
    const yearlySummary = activeSingleData.yearlySummary || {};
    const labels = Object.keys(history).sort();
    const isNet = chartTaxMode === "net";

    const getNetRatio = (m) => {
      const year = m.split("-")[0];
      const yData = yearlySummary[year];
      if (!yData || !yData.grossKRW || yData.grossKRW <= 0) return 0.85;
      return yData.netKRW / yData.grossKRW;
    };

    // 종목 목록 추출 (holdings 우선, 없으면 allocations)
    const symbols = Object.keys(activeSingleData.holdings || {}).length > 0
      ? Object.keys(activeSingleData.holdings)
      : allocations.map((a) => a.symbol);

    // 종목별 테마 컬러 팔레트
    const PALETTE = [
      { bg: "rgba(99, 102, 241, 0.85)", hover: "rgba(79, 70, 229, 1)" },   // Indigo
      { bg: "rgba(16, 185, 129, 0.85)", hover: "rgba(5, 150, 105, 1)" },   // Emerald
      { bg: "rgba(245, 158, 11, 0.85)", hover: "rgba(217, 119, 6, 1)" },   // Amber
      { bg: "rgba(236, 72, 153, 0.85)", hover: "rgba(219, 39, 119, 1)" },  // Pink
      { bg: "rgba(6, 182, 212, 0.85)", hover: "rgba(8, 145, 178, 1)" },    // Cyan
      { bg: "rgba(139, 92, 246, 0.85)", hover: "rgba(124, 58, 237, 1)" },  // Violet
      { bg: "rgba(249, 115, 22, 0.85)", hover: "rgba(234, 88, 12, 1)" },   // Orange
      { bg: "rgba(20, 184, 166, 0.85)", hover: "rgba(13, 148, 136, 1)" }   // Teal
    ];

    const hasBySymbol = labels.some((m) => history[m]?.bySymbol && Object.keys(history[m].bySymbol).length > 0);

    if (hasBySymbol && symbols.length > 1) {
      // 2개 이상 종목 선택 시 종목별 스택(Stacked) 바 차트 구성
      const datasets = symbols.map((sym, idx) => {
        const color = PALETTE[idx % PALETTE.length];
        return {
          label: isNet ? `${sym} 실수령` : `${sym} 세전 배당금`,
          data: labels.map((m) => {
            const gross = history[m]?.bySymbol?.[sym]?.grossKRW || 0;
            const ratio = getNetRatio(m);
            return isNet ? Math.round(gross * ratio) : Math.round(gross);
          }),
          backgroundColor: color.bg,
          hoverBackgroundColor: color.hover,
          stack: "monthlyStack",
          borderRadius: idx === symbols.length - 1 ? 4 : 0
        };
      });

      return {
        labels,
        datasets,
        isStacked: true,
        chartTaxMode
      };
    }

    // 단일 종목이거나 bySymbol이 없을 때 기본 바 차트
    const isDca = investmentType === "DCA" || compareSubTab === "dca";
    const singleLabel = isNet
      ? (symbols.length === 1 ? `${symbols[0]} 월 실수령액` : "월 실수령 배당금")
      : (symbols.length === 1 ? `${symbols[0]} 월 세전 배당금` : "월 세전 배당금");
    return {
      labels,
      datasets: [
        {
          label: singleLabel,
          data: labels.map((m) => {
            const gross = history[m]?.grossKRW || 0;
            const ratio = getNetRatio(m);
            return isNet ? Math.round(gross * ratio) : Math.round(gross);
          }),
          backgroundColor: isDca ? "rgba(16, 185, 129, 0.75)" : "rgba(99, 102, 241, 0.75)",
          hoverBackgroundColor: isDca ? "rgba(5, 150, 105, 0.95)" : "rgba(79, 70, 229, 0.95)",
          borderRadius: 6
        }
      ],
      isStacked: false,
      chartTaxMode
    };
  }, [activeSingleData, investmentType, compareSubTab, allocations, chartTaxMode]);

  // 4. 단독 모드: 연도별 세금/건보료 공제 스택 차트
  const singleYearlyChartData = useMemo(() => {
    if (!activeSingleData || !activeSingleData.yearlySummary) return null;
    const summary = activeSingleData.yearlySummary;
    const years = Object.keys(summary).sort();

    return {
      labels: years.map((y) => `${y}년`),
      datasets: [
        {
          label: "내 손에 남은 실수령액",
          data: years.map((y) => summary[y]?.netKRW || 0),
          backgroundColor: "#10b981",
          borderRadius: 4
        },
        {
          label: "미국 원천징수세 (15%)",
          data: years.map((y) => summary[y]?.usTaxKRW || 0),
          backgroundColor: "#3b82f6",
          borderRadius: 4
        },
        {
          label: "종합소득세 (2천 초과 누진분)",
          data: years.map((y) => summary[y]?.extraTaxKRW || 0),
          backgroundColor: "#f59e0b",
          borderRadius: 4
        },
        {
          label: "건강보험료 타격",
          data: years.map((y) => summary[y]?.healthInsKRW || 0),
          backgroundColor: "#ef4444",
          borderRadius: 4
        }
      ]
    };
  }, [activeSingleData]);

  // 연도별 상세 테이블 합계 계산
  const activeYearlyTotals = useMemo(() => {
    if (!activeSingleData || !activeSingleData.yearlySummary) return null;
    const summary = activeSingleData.yearlySummary;
    const years = Object.keys(summary);
    return years.reduce(
      (acc, y) => {
        acc.grossKRW += summary[y]?.grossKRW || 0;
        acc.usTaxKRW += summary[y]?.usTaxKRW || 0;
        acc.extraTaxKRW += summary[y]?.extraTaxKRW || 0;
        acc.healthInsKRW += summary[y]?.healthInsKRW || 0;
        acc.netKRW += summary[y]?.netKRW || 0;
        return acc;
      },
      { grossKRW: 0, usTaxKRW: 0, extraTaxKRW: 0, healthInsKRW: 0, netKRW: 0 }
    );
  }, [activeSingleData]);

  // COMPARE 모드: 연도 목록 및 종목 목록
  const compareYears = useMemo(() => {
    if (!result || !result.lumpSum || !result.dca) return [];
    const lumpY = result.lumpSum.yearlySummary || {};
    const dcaY = result.dca.yearlySummary || {};
    return Array.from(new Set([...Object.keys(lumpY), ...Object.keys(dcaY)])).sort();
  }, [result]);

  const compareSymbols = useMemo(() => {
    if (!result || !result.lumpSum || !result.dca) return [];
    const lumpH = result.lumpSum.holdings || {};
    const dcaH = result.dca.holdings || {};
    return Array.from(new Set([...Object.keys(lumpH), ...Object.keys(dcaH)])).sort();
  }, [result]);

  const lumpTotals = useMemo(() => {
    if (!result || !result.lumpSum?.yearlySummary) return { grossKRW: 0, netKRW: 0 };
    const summary = result.lumpSum.yearlySummary;
    return Object.keys(summary).reduce(
      (acc, y) => {
        acc.grossKRW += summary[y]?.grossKRW || 0;
        acc.netKRW += summary[y]?.netKRW || 0;
        return acc;
      },
      { grossKRW: 0, netKRW: 0 }
    );
  }, [result]);

  const dcaTotals = useMemo(() => {
    if (!result || !result.dca?.yearlySummary) return { grossKRW: 0, netKRW: 0 };
    const summary = result.dca.yearlySummary;
    return Object.keys(summary).reduce(
      (acc, y) => {
        acc.grossKRW += summary[y]?.grossKRW || 0;
        acc.netKRW += summary[y]?.netKRW || 0;
        return acc;
      },
      { grossKRW: 0, netKRW: 0 }
    );
  }, [result]);

  return (
    <div className="wafire-page">
      <Container className="pt-4">
        {/* =================================================================== */}
        {/* 1. 상단 히어로 배너 */}
        {/* =================================================================== */}
        <div className="wafire-hero-banner">
          <div className="wafire-hero-badge">
            <FaFire /> AMERICAN DIVIDEND ETF FIRE SIMULATOR
          </div>
          <h1 className="wafire-hero-title">
            와이어 <span style={{ color: "#f43f5e" }}>(와!파이어)</span>
          </h1>
          <p className="wafire-hero-desc">
            미국 월배당 ETF 포트폴리오를 기반으로 조기은퇴(FIRE) 가능성을 검증합니다.
            환율, 주가 변동성, 미국 15% 세금은 물론 <strong>2,000만 원 초과 금융소득종합과세와 건보료 폭탄</strong>까지
            실전 백테스트로 정밀 계산해드립니다
          </p>
        </div>

        {/* =================================================================== */}
        {/* 2. 투자 방식 선택 (3단 토글 스위치) */}
        {/* =================================================================== */}
        <div className="wafire-mode-toggle">
          <button
            type="button"
            className={`wafire-mode-btn ${investmentType === "LUMP_SUM" ? "active" : ""}`}
            onClick={() => handleModeSwitch("LUMP_SUM")}
          >
            <FaLayerGroup /> 거치식 (일시 투자)
          </button>
          <button
            type="button"
            className={`wafire-mode-btn ${investmentType === "DCA" ? "active" : ""}`}
            onClick={() => handleModeSwitch("DCA")}
          >
            <FaHandHoldingUsd /> 적립식 (월 분할 적립)
          </button>
          <button
            type="button"
            className={`wafire-mode-btn compare ${investmentType === "COMPARE" ? "active" : ""}`}
            onClick={() => handleModeSwitch("COMPARE")}
          >
            <FaTrophy /> ⚔️ 맞대결 비교
          </button>
        </div>

        {/* =================================================================== */}
        {/* 3. 컨트롤 패널 (입력 폼) */}
        {/* =================================================================== */}
        <Row>
          {/* 좌측: 조건 설정 */}
          <Col lg={5}>
            <div className="wafire-card">
              <div className="wafire-card-title">
                <span className="d-flex align-items-center gap-2">
                  <span className="wafire-card-title-icon"><FaCoins /></span>
                  {investmentType === "COMPARE"
                    ? "맞대결 투자 조건 설정"
                    : investmentType === "DCA"
                    ? "적립식 투자 조건 설정"
                    : "거치식 투자 조건 설정"}
                </span>
              </div>

              {/* 거치식 자본금 입력 (LUMP_SUM 또는 COMPARE) */}
              {(investmentType === "LUMP_SUM" || investmentType === "COMPARE") && (
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold text-muted d-flex justify-content-between">
                    <span>{investmentType === "COMPARE" ? "거치식 일시 투자금 (원화)" : "총 투자 자본금 (원화)"}</span>
                    <span className="text-primary fw-bold">{formatKoreanAmount(totalCapital)}</span>
                  </Form.Label>
                  <div className="wafire-capital-display">
                    {formatKRW(totalCapital)} <span style={{ fontSize: "1rem", color: "#64748b" }}>원</span>
                  </div>
                  <Form.Control
                    type="number"
                    step="10000000"
                    min="10000000"
                    value={totalCapital}
                    onChange={(e) => setTotalCapital(Number(e.target.value) || 0)}
                    className="rounded-3"
                  />
                  <div className="wafire-quick-btn-group">
                    <button type="button" className="wafire-quick-btn" onClick={() => handleAddCapital(10000000)}>
                      +1,000만
                    </button>
                    <button type="button" className="wafire-quick-btn" onClick={() => handleAddCapital(50000000)}>
                      +5,000만
                    </button>
                    <button type="button" className="wafire-quick-btn" onClick={() => handleAddCapital(100000000)}>
                      +1억
                    </button>
                    <button type="button" className="wafire-quick-btn" onClick={() => setTotalCapital(300000000)}>
                      기본(3억)
                    </button>
                  </div>
                </Form.Group>
              )}

              {/* 적립식 월 납입액 입력 (DCA 또는 COMPARE) */}
              {(investmentType === "DCA" || investmentType === "COMPARE") && (
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold text-muted d-flex justify-content-between">
                    <span>매월 적립 납입액 (원화)</span>
                    <span className="text-success fw-bold">매월 {formatKoreanAmount(monthlyDeposit)}</span>
                  </Form.Label>
                  <div className="wafire-capital-display" style={{ color: "#059669" }}>
                    {formatKRW(monthlyDeposit)} <span style={{ fontSize: "1rem", color: "#64748b" }}>원 / 월</span>
                  </div>
                  <Form.Control
                    type="number"
                    step="500000"
                    min="100000"
                    value={monthlyDeposit}
                    onChange={(e) => setMonthlyDeposit(Number(e.target.value) || 0)}
                    className="rounded-3"
                  />
                  <div className="wafire-quick-btn-group">
                    <button type="button" className="wafire-quick-btn" onClick={() => handleAddMonthlyDeposit(500000)}>
                      +50만
                    </button>
                    <button type="button" className="wafire-quick-btn" onClick={() => handleAddMonthlyDeposit(1000000)}>
                      +100만
                    </button>
                    <button type="button" className="wafire-quick-btn" onClick={() => setMonthlyDeposit(2500000)}>
                      기본(250만)
                    </button>
                    <button type="button" className="wafire-quick-btn" onClick={() => setMonthlyDeposit(5000000)}>
                      500만
                    </button>
                  </div>
                </Form.Group>
              )}

              {/* 백테스트 기간 설정 (시작 월 ~ 종료 월) */}
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-muted d-flex align-items-center justify-content-between">
                  <span className="d-flex align-items-center gap-1">
                    <FaCalendarAlt /> 백테스트 조회 기간 (월 단위)
                  </span>
                  <span className="text-secondary small">
                    최대 5년 범위 ({minMonth} ~ {maxMonth})
                  </span>
                </Form.Label>
                <Row className="g-2">
                  <Col xs={6}>
                    <WaFireMonthInput
                      label="시작 월"
                      min={minMonth}
                      max={maxMonth}
                      value={startMonth}
                      onChange={handleStartMonthChange}
                    />
                  </Col>
                  <Col xs={6}>
                    <WaFireMonthInput
                      label="종료 월"
                      min={startMonth}
                      max={maxMonth}
                      value={endMonth}
                      onChange={handleEndMonthChange}
                    />
                  </Col>
                </Row>
                <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="wafire-quick-btn"
                      onClick={() => {
                        setStartMonth("2023-01" < minMonth ? minMonth : "2023-01");
                        setEndMonth(maxMonth);
                      }}
                    >
                      2023 ~ 이번 달
                    </button>
                    <button
                      type="button"
                      className="wafire-quick-btn"
                      onClick={() => {
                        setStartMonth("2024-01" > maxMonth ? maxMonth : "2024-01");
                        setEndMonth(maxMonth);
                      }}
                    >
                      2024 ~ 이번 달
                    </button>
                    <button
                      type="button"
                      className="wafire-quick-btn"
                      onClick={() => {
                        setStartMonth(minMonth);
                        setEndMonth(maxMonth);
                      }}
                    >
                      최대 5년 전체
                    </button>
                  </div>
                  <span className="text-muted" style={{ fontSize: "0.74rem" }}>
                    ※ 시작월 1일 ~ 종료월 말일 기준으로 시뮬레이션됩니다
                  </span>
                </div>
              </Form.Group>

              {/* 적립식 총 예상 누적 투자 원금 안내 카드 */}
              {(investmentType === "DCA" || investmentType === "COMPARE") && (
                <div
                  className="p-3 mb-3 rounded-3"
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <span className="small fw-bold text-success d-flex align-items-center gap-1">
                      <FaCoins /> {investmentType === "COMPARE" ? "적립식 총 투입 예정 원금" : "총 누적 투자 예정 원금"}
                    </span>
                    <span className="fw-bolder fs-5 text-success">
                      {formatKoreanAmount(totalDcaCapital)}
                    </span>
                  </div>
                  <div className="d-flex align-items-center justify-content-between text-muted" style={{ fontSize: "0.78rem" }}>
                    <span>설정 기간: <strong className="text-dark">{dcaPeriodText}</strong></span>
                    <span>(매월 {formatKoreanAmount(monthlyDeposit)} × {dcaMonths}개월)</span>
                  </div>
                </div>
              )}

              {/* 투자자 신분 토글 */}
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-muted">
                  투자자 신분 (건보료/종합과세 계산 기준)
                </Form.Label>
                <div className="wafire-status-toggle">
                  <button
                    type="button"
                    className={`wafire-status-btn ${status === "UNEMPLOYED" ? "active" : ""}`}
                    onClick={() => setStatus("UNEMPLOYED")}
                  >
                    <span className="d-flex align-items-center gap-1">
                      <FaUmbrellaBeach /> 백수 / 은퇴자
                    </span>
                    <span className="badge-sub">피부양자 / 지역가입자</span>
                  </button>
                  <button
                    type="button"
                    className={`wafire-status-btn ${status === "EMPLOYED" ? "active" : ""}`}
                    onClick={() => setStatus("EMPLOYED")}
                  >
                    <span className="d-flex align-items-center gap-1">
                      <FaUserTie /> 직장인
                    </span>
                    <span className="badge-sub">직장가입자 (소득월액)</span>
                  </button>
                </div>

                {/* 신분별 안내 배너 */}
                <AnimatePresence mode="wait">
                  {status === "UNEMPLOYED" ? (
                    <motion.div
                      key="unemployed-alert"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      className="wafire-tax-alert unemployed"
                    >
                      <FaExclamationTriangle className="me-1" />
                      <strong>피부양자 박탈 경고:</strong> 연 배당소득이 <strong>2,000만 원</strong>을 초과하면
                      피부양자 자격이 박탈되어 지역가입자로 전환되며 건보료 전액이 부과됩니다!
                    </motion.div>
                  ) : (
                    <motion.div
                      key="employed-alert"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 0 }}
                      className="wafire-tax-alert employed"
                    >
                      <FaShieldAlt className="me-1" />
                      <strong>소득월액보험료 안내:</strong> 월급 외 금융소득이 <strong>2,000만 원</strong>을 초과하면,
                      초과분에 대해 약 7~8%의 건보료가 회사 월급과 별개로 추가 부과됩니다.
                    </motion.div>
                  )}
                </AnimatePresence>
              </Form.Group>

              {/* 타 소득 */}
              <Form.Group className="mb-2">
                <Form.Label className="small fw-bold text-muted d-flex justify-content-between">
                  <span>타 연간 소득 (근로소득/사업 등)</span>
                  <span className="text-secondary">{formatKoreanAmount(otherAnnualIncome)}</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="5000000"
                  min="0"
                  placeholder="0"
                  value={otherAnnualIncome}
                  onChange={(e) => setOtherAnnualIncome(Number(e.target.value) || 0)}
                  className="rounded-3"
                />
              </Form.Group>
            </div>
          </Col>

          {/* 우측: ETF 포트폴리오 비중 배분 인터페이스 */}
          <Col lg={7}>
            <div className="wafire-card">
              <div className="wafire-card-title">
                <span className="d-flex align-items-center gap-2">
                  <span className="wafire-card-title-icon"><FaBalanceScale /></span>
                  ETF 포트폴리오 비중 배분
                </span>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill px-3 fw-bold"
                    onClick={handleEqualWeight}
                  >
                    1/N 균등 배분
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary rounded-pill px-3 fw-bold"
                    onClick={handleAutoAdjust}
                  >
                    100% 자동 맞춤
                  </button>
                </div>
              </div>

              {/* 선택된 ETF 슬라이더 목록 */}
              <div className="wafire-allocations-list">
                {allocations.map((item, idx) => {
                  const meta = ETF_DATABASE[item.symbol] || {};
                  const percentVal = Math.round((item.ratio || 0) * 100);
                  const allocatedKRW = totalCapital * (item.ratio || 0);
                  const monthlyAllocatedKRW = monthlyDeposit * (item.ratio || 0);
                  const totalDcaAllocatedKRW = totalDcaCapital * (item.ratio || 0);

                  return (
                    <div key={item.symbol} className="wafire-allocation-card">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="wafire-symbol-badge">
                          <span>{item.symbol}</span>
                          <span className="wafire-symbol-tag">{meta.tag || "ETF"}</span>
                          <span className="wafire-symbol-yield">예상 {meta.yieldEst}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {investmentType === "LUMP_SUM" && (
                            <span className="small text-muted fw-bold">
                              {formatKoreanAmount(allocatedKRW)}
                            </span>
                          )}
                          {investmentType === "DCA" && (
                            <span className="small text-success fw-bold" title={`총 누적 투자액: ${formatKoreanAmount(totalDcaAllocatedKRW)}`}>
                              월 {formatKoreanAmount(monthlyAllocatedKRW)}
                              <span className="text-secondary fw-normal ms-1" style={{ fontSize: "0.75rem" }}>
                                (총 {formatKoreanAmount(totalDcaAllocatedKRW)})
                              </span>
                            </span>
                          )}
                          {investmentType === "COMPARE" && (
                            <span className="small text-muted fw-bold">
                              거치 {formatKoreanAmount(allocatedKRW)}
                              <span className="text-success ms-1">
                                · 월 {formatKoreanAmount(monthlyAllocatedKRW)}
                              </span>
                            </span>
                          )}
                          <div className="wafire-slider-val">{percentVal}%</div>
                          {allocations.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-sm text-danger p-0 ms-2"
                              onClick={() => handleRemoveSymbol(idx)}
                              title={`${item.symbol} 삭제`}
                            >
                              <FaTrashAlt />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="small text-muted mb-2 text-truncate" title={meta.desc}>
                        {meta.desc}
                      </div>

                      <div className="d-flex align-items-center gap-3">
                        <Form.Range
                          min="0"
                          max="100"
                          step="1"
                          value={percentVal}
                          onChange={(e) => handleRatioChange(idx, e.target.value)}
                          className="flex-grow-1"
                        />
                        <Form.Control
                          type="number"
                          min="0"
                          max="100"
                          style={{ width: "70px" }}
                          value={percentVal}
                          onChange={(e) => handleRatioChange(idx, e.target.value)}
                          className="text-center rounded-3 fw-bold py-1"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ETF 종목 추가 드롭다운 */}
              {unselectedSymbols.length > 0 && (
                <div className="mt-3 d-flex align-items-center gap-2">
                  <Form.Select
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddSymbol(e.target.value);
                        e.target.value = "";
                      }
                    }}
                    className="rounded-3"
                  >
                    <option value="" disabled>
                      + 추가할 미국 ETF 선택 (배당/인컴 및 QLD, QQQM)...
                    </option>
                    {unselectedSymbols.map((sym) => (
                      <option key={sym} value={sym}>
                        {sym} ({ETF_DATABASE[sym]?.name} • {ETF_DATABASE[sym]?.tag})
                      </option>
                    ))}
                  </Form.Select>
                </div>
              )}

              {/* 비중 합계 검증 배지 */}
              <div className={`wafire-ratio-sum-box ${isRatioValid ? "valid" : "invalid"}`}>
                <div className="d-flex align-items-center gap-2">
                  {isRatioValid ? <FaCheckCircle /> : <FaExclamationTriangle />}
                  <span>
                    {isRatioValid
                      ? "비중 합계가 정확히 100%입니다."
                      : `비중의 합이 100%여야 합니다 (현재 ${totalPercent}%)`}
                  </span>
                </div>
                <div className="fs-5">{totalPercent}%</div>
              </div>

              {errorMsg && (
                <Alert variant="danger" className="mt-3 py-2 small rounded-3">
                  {errorMsg}
                </Alert>
              )}

              {/* 실행 버튼 */}
              <button
                type="button"
                className="wafire-submit-btn"
                onClick={() => fetchSimulation()}
                disabled={loading || !isRatioValid}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    n8n 금융 엔진에서 실전 백테스트 연산 중...
                  </>
                ) : (
                  <>
                    <FaFire /> {investmentType === "COMPARE" ? "거치 vs 적립 맞대결 시뮬레이션 실행" : "회사 탈출 각 재보기"}
                  </>
                )}
              </button>
            </div>
          </Col>
        </Row>

        {/* =================================================================== */}
        {/* 4. 시뮬레이션 결과 섹션 */}
        {/* =================================================================== */}
        {result && (
          <motion.div
            key={investmentType}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-4"
          >
            {/* =============================================================== */}
            {/* A. COMPARE (맞대결 모드) 전용 대시보드 */}
            {/* =============================================================== */}
            {investmentType === "COMPARE" && result.comparison && (
              <div>
                {/* 1. 승자 발표 배너 */}
                <div className="wafire-winner-banner">
                  <div className={`wafire-winner-badge ${result.comparison.winner === "LUMP_SUM" ? "lumpsum" : "dca"}`}>
                    <FaTrophy /> {result.comparison.winner === "LUMP_SUM" ? "거치식 (LUMP_SUM) 우세" : "적립식 (DCA) 방어 승리"}
                  </div>
                  <h2 className="wafire-winner-title">
                    {result.comparison.winner === "LUMP_SUM"
                      ? "🏆 거치식(일시불) 투자 전략의 압승!"
                      : "🛡️ 적립식(DCA) 코스트 에버리징 전략의 승리!"}
                  </h2>
                  <p className="wafire-winner-desc">
                    {result.comparison.comment}
                  </p>
                  <div className="wafire-winner-stats">
                    <div className="wafire-winner-stat-pill">
                      <span>최종 평가액 격차</span>
                      <span>+{formatKoreanAmount(result.comparison.diffEvaluationKRW)}</span>
                    </div>
                    <div className="wafire-winner-stat-pill">
                      <span>누적 실수령 배당금 격차</span>
                      <span>+{formatKoreanAmount(result.comparison.diffNetDivKRW)}</span>
                    </div>
                  </div>
                </div>

                {/* 2. 1:1 맞대결 핵심 지표 그리드 (Versus Cards) */}
                <div className="wafire-versus-grid">
                  {/* 지표 1: 총 투자 원금 */}
                  <div className="wafire-versus-card">
                    <div className="wafire-versus-label">
                      <span>총 투입 원금</span>
                      <FaCoins />
                    </div>
                    <div className="wafire-versus-comparison">
                      <div className="wafire-side-box lump">
                        <span className="wafire-side-tag">거치식</span>
                        <span className="wafire-side-val">{formatKoreanAmount(result.lumpSum?.summary?.totalInvestedKRW)}</span>
                        <span className="wafire-side-sub">초기 일시 투자</span>
                      </div>
                      <span className="wafire-vs-divider">VS</span>
                      <div className="wafire-side-box dca">
                        <span className="wafire-side-tag">적립식</span>
                        <span className="wafire-side-val">{formatKoreanAmount(result.dca?.summary?.totalInvestedKRW)}</span>
                        <span className="wafire-side-sub">월 분할 납입 합계</span>
                      </div>
                    </div>
                  </div>

                  {/* 지표 2: 최종 평가액 & 수익률 */}
                  <div className="wafire-versus-card">
                    <div className="wafire-versus-label">
                      <span>최종 평가액 & 수익률</span>
                      <FaChartLine />
                    </div>
                    <div className="wafire-versus-comparison">
                      <div className="wafire-side-box lump">
                        <span className="wafire-side-tag">거치식</span>
                        <span className="wafire-side-val">{formatKoreanAmount(result.lumpSum?.summary?.currentEvaluationKRW)}</span>
                        <span className="wafire-side-sub" style={{ color: "#4f46e5", fontWeight: 700 }}>
                          +{result.lumpSum?.summary?.capitalReturnRate}%
                        </span>
                      </div>
                      <span className="wafire-vs-divider">VS</span>
                      <div className="wafire-side-box dca">
                        <span className="wafire-side-tag">적립식</span>
                        <span className="wafire-side-val">{formatKoreanAmount(result.dca?.summary?.currentEvaluationKRW)}</span>
                        <span className="wafire-side-sub" style={{ color: "#059669", fontWeight: 700 }}>
                          +{result.dca?.summary?.capitalReturnRate}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 지표 3: 누적 실수령 배당금 */}
                  <div className="wafire-versus-card">
                    <div className="wafire-versus-label">
                      <span>누적 총 실수령 배당금</span>
                      <FaMoneyBillWave />
                    </div>
                    <div className="wafire-versus-comparison">
                      <div className="wafire-side-box lump">
                        <span className="wafire-side-tag">거치식</span>
                        <span className="wafire-side-val">{formatKoreanAmount(result.lumpSum?.summary?.totalNetDivKRW)}</span>
                        <span className="wafire-side-sub">초기 수량 복리</span>
                      </div>
                      <span className="wafire-vs-divider">VS</span>
                      <div className="wafire-side-box dca">
                        <span className="wafire-side-tag">적립식</span>
                        <span className="wafire-side-val">{formatKoreanAmount(result.dca?.summary?.totalNetDivKRW)}</span>
                        <span className="wafire-side-sub">점진적 증가</span>
                      </div>
                    </div>
                  </div>

                  {/* 지표 4: 월평균 실수령액 */}
                  <div className="wafire-versus-card">
                    <div className="wafire-versus-label">
                      <span>월평균 실수령액</span>
                      <FaFire />
                    </div>
                    <div className="wafire-versus-comparison">
                      <div className="wafire-side-box lump">
                        <span className="wafire-side-tag">거치식</span>
                        <span className="wafire-side-val" title={`월 ${formatKRW(result.lumpSum?.summary?.avgMonthlyNetKRW)}원`}>
                          {formatKRW(result.lumpSum?.summary?.avgMonthlyNetKRW)}원
                        </span>
                        <span className="wafire-side-sub">초기 균등 인컴</span>
                      </div>
                      <span className="wafire-vs-divider">VS</span>
                      <div className="wafire-side-box dca">
                        <span className="wafire-side-tag">적립식</span>
                        <span className="wafire-side-val" title={`월 ${formatKRW(result.dca?.summary?.avgMonthlyNetKRW)}원`}>
                          {formatKRW(result.dca?.summary?.avgMonthlyNetKRW)}원
                        </span>
                        <span className="wafire-side-sub">점진적 평균</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. 맞대결 시각화 차트 2종 (듀얼 그룹 차트) */}
                <Row className="mb-4">
                  {/* 차트 1: 월별 배당금 맞대결 */}
                  <Col lg={7} className="mb-4 mb-lg-0">
                    <div className="wafire-card h-100">
                      <div className="wafire-card-title d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <span className="d-flex align-items-center gap-2">
                          <span className="wafire-card-title-icon"><FaChartLine /></span>
                          월별 맞대결 추이 ({chartTaxMode === "net" ? "세후 실수령" : "세전"})
                        </span>
                        <div className="wafire-tax-toggle">
                          <button
                            type="button"
                            className={`wafire-tax-btn ${chartTaxMode === "net" ? "active net" : ""}`}
                            onClick={() => setChartTaxMode("net")}
                          >
                            💧 세후 실수령
                          </button>
                          <button
                            type="button"
                            className={`wafire-tax-btn ${chartTaxMode === "gross" ? "active" : ""}`}
                            onClick={() => setChartTaxMode("gross")}
                          >
                            📦 세전
                          </button>
                        </div>
                      </div>
                      <div className="wafire-chart-wrapper">
                        {compareMonthlyChartData && (
                          <Bar
                            data={compareMonthlyChartData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              interaction: {
                                mode: "index",
                                intersect: false
                              },
                              plugins: {
                                legend: { position: "top", labels: { font: { size: 11, weight: "bold" }, boxWidth: 14 } },
                                tooltip: {
                                  callbacks: {
                                    label: (ctx) => {
                                      const month = ctx.label;
                                      const isLump = ctx.datasetIndex === 0;
                                      const hist = isLump ? result?.lumpSum?.monthlyHistory?.[month] : result?.dca?.monthlyHistory?.[month];
                                      const year = month.split("-")[0];
                                      const ySummary = isLump ? result?.lumpSum?.yearlySummary?.[year] : result?.dca?.yearlySummary?.[year];
                                      const netRatio = ySummary?.grossKRW > 0 ? ySummary.netKRW / ySummary.grossKRW : 0.85;

                                      const grossKRW = Math.round(hist?.grossKRW ?? ctx.raw ?? 0);
                                      const netKRW = Math.round(grossKRW * netRatio);
                                      const prefix = isLump ? "거치식" : "적립식";

                                      if (chartTaxMode === "net") {
                                        return ` ${prefix} 실수령: ${netKRW.toLocaleString()}원 (세전 ${grossKRW.toLocaleString()}원)`;
                                      } else {
                                        const usd = hist?.grossUSD != null ? formatUSD(hist.grossUSD) : null;
                                        return usd
                                          ? ` ${prefix} 세전: ${grossKRW.toLocaleString()}원 ($${usd})`
                                          : ` ${prefix} 세전: ${grossKRW.toLocaleString()}원`;
                                      }
                                    },
                                    afterLabel: (ctx) => {
                                      const month = ctx.label;
                                      const isLump = ctx.datasetIndex === 0;
                                      const hist = isLump ? result?.lumpSum?.monthlyHistory?.[month] : result?.dca?.monthlyHistory?.[month];
                                      if (!hist?.bySymbol) return [];
                                      const entries = Object.entries(hist.bySymbol);
                                      if (entries.length <= 1) return [];

                                      const year = month.split("-")[0];
                                      const ySummary = isLump ? result?.lumpSum?.yearlySummary?.[year] : result?.dca?.yearlySummary?.[year];
                                      const netRatio = ySummary?.grossKRW > 0 ? ySummary.netKRW / ySummary.grossKRW : 0.85;

                                      return entries.map(([sym, val]) => {
                                        const gK = Math.round(val.grossKRW || 0);
                                        const nK = Math.round(gK * netRatio);
                                        return chartTaxMode === "net"
                                          ? `   └ ${sym}: ${nK.toLocaleString()}원`
                                          : `   └ ${sym}: ${gK.toLocaleString()}원`;
                                      });
                                    }
                                  }
                                }
                              },
                              scales: {
                                x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 45 } },
                                y: {
                                  grid: { color: "rgba(226, 232, 240, 0.6)" },
                                  ticks: { callback: (val) => `${Math.round(val / 10000)}만` }
                                }
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* 차트 2: 연도별 실수령액 맞대결 */}
                  <Col lg={5}>
                    <div className="wafire-card h-100">
                      <div className="wafire-card-title">
                        <span className="d-flex align-items-center gap-2">
                          <span className="wafire-card-title-icon"><FaFileInvoiceDollar /></span>
                          연도별 실수령액 맞대결
                        </span>
                      </div>
                      <div className="wafire-chart-wrapper">
                        {compareYearlyChartData && (
                          <Bar
                            data={compareYearlyChartData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: { position: "top", labels: { font: { size: 11, weight: "bold" } } },
                                tooltip: {
                                  callbacks: {
                                    label: (ctx) => ` ${ctx.dataset.label}: ${formatKRW(ctx.raw)} 원`
                                  }
                                }
                              },
                              scales: {
                                x: { grid: { display: false } },
                                y: {
                                  grid: { color: "rgba(226, 232, 240, 0.6)" },
                                  ticks: { callback: (val) => `${Math.round(val / 10000)}만` }
                                }
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* 4. 서브 탭 (거치식/적립식 개별 상세 데이터 검토) */}
                <div className="wafire-tab-group">
                  <button
                    type="button"
                    className={`wafire-tab-btn ${compareSubTab === "summary" ? "active" : ""}`}
                    onClick={() => setCompareSubTab("summary")}
                  >
                    ⚔️ 맞대결 종합 요약
                  </button>
                  <button
                    type="button"
                    className={`wafire-tab-btn ${compareSubTab === "lumpSum" ? "active" : ""}`}
                    onClick={() => setCompareSubTab("lumpSum")}
                  >
                    📦 거치식 상세 데이터 (테이블 & 보유현황)
                  </button>
                  <button
                    type="button"
                    className={`wafire-tab-btn ${compareSubTab === "dca" ? "active" : ""}`}
                    onClick={() => setCompareSubTab("dca")}
                  >
                    💧 적립식 상세 데이터 (테이블 & 보유현황)
                  </button>
                </div>

                {/* 5. 맞대결 종합 요약 탭 내용 */}
                {compareSubTab === "summary" && (
                  <div>
                    {/* 연도별 맞대결 종합 비교 테이블 */}
                    <div className="wafire-card mb-4">
                      <div className="wafire-card-title">
                        <span className="d-flex align-items-center gap-2">
                          <span className="wafire-card-title-icon"><FaMoneyBillWave /></span>
                          연도별 거치식 vs 적립식 실수령 배당금 맞대결 비교
                        </span>
                        <span className="small text-muted">단위: 원화(₩)</span>
                      </div>

                      <div className="wafire-table-responsive">
                        <table className="wafire-table">
                          <thead>
                            <tr>
                              <th>연도</th>
                              <th>거치식 세전 배당</th>
                              <th>거치식 실수령액</th>
                              <th>적립식 세전 배당</th>
                              <th>적립식 실수령액</th>
                              <th>실수령 격차 (거치 - 적립)</th>
                              <th>연도별 우세</th>
                            </tr>
                          </thead>
                          <tbody>
                            {compareYears.map((year) => {
                              const lump = result.lumpSum?.yearlySummary?.[year] || { grossKRW: 0, netKRW: 0 };
                              const dca = result.dca?.yearlySummary?.[year] || { grossKRW: 0, netKRW: 0 };
                              const diffNet = lump.netKRW - dca.netKRW;
                              const isLumpAhead = diffNet >= 0;

                              return (
                                <tr key={year}>
                                  <td>{year}년</td>
                                  <td>{formatKRW(lump.grossKRW)} 원</td>
                                  <td className="fw-bold text-primary">{formatKRW(lump.netKRW)} 원</td>
                                  <td>{formatKRW(dca.grossKRW)} 원</td>
                                  <td className="fw-bold text-success">{formatKRW(dca.netKRW)} 원</td>
                                  <td style={{ color: isLumpAhead ? "#4f46e5" : "#059669", fontWeight: 700 }}>
                                    {isLumpAhead ? "+" : ""}{formatKRW(diffNet)} 원
                                  </td>
                                  <td>
                                    <span className={`badge ${isLumpAhead ? "bg-primary-subtle text-primary" : "bg-success-subtle text-success"} fw-bold`}>
                                      {isLumpAhead ? "거치식 +" : "적립식 +"}{formatKRW(Math.abs(diffNet))}원
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="total-row">
                              <td>총 합계</td>
                              <td>{formatKRW(lumpTotals.grossKRW)} 원</td>
                              <td className="fw-bold text-primary fs-6">{formatKRW(lumpTotals.netKRW)} 원</td>
                              <td>{formatKRW(dcaTotals.grossKRW)} 원</td>
                              <td className="fw-bold text-success fs-6">{formatKRW(dcaTotals.netKRW)} 원</td>
                              <td style={{ color: lumpTotals.netKRW >= dcaTotals.netKRW ? "#4f46e5" : "#059669", fontWeight: 800 }}>
                                {lumpTotals.netKRW >= dcaTotals.netKRW ? "+" : ""}{formatKRW(lumpTotals.netKRW - dcaTotals.netKRW)} 원
                              </td>
                              <td>
                                <span className="badge bg-primary text-white fw-bold">
                                  {lumpTotals.netKRW >= dcaTotals.netKRW ? "거치식 우세 🏆" : "적립식 우세 🛡️"}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* ETF 종목별 보유 수량 & 투자원금 & 누적 배당금 1:1 맞대결 */}
                    <div className="wafire-card">
                      <div className="wafire-card-title">
                        <span className="d-flex align-items-center gap-2">
                          <span className="wafire-card-title-icon"><FaExchangeAlt /></span>
                          ETF 종목별 수량 & 누적 배당금 맞대결
                        </span>
                      </div>

                      <div className="wafire-holdings-grid">
                        {compareSymbols.map((sym) => {
                          const lumpH = result.lumpSum?.holdings?.[sym] || {};
                          const dcaH = result.dca?.holdings?.[sym] || {};
                          const diffShares = (lumpH.shares || 0) - (dcaH.shares || 0);
                          const diffDiv = (lumpH.totalDividendKRW || 0) - (dcaH.totalDividendKRW || 0);

                          return (
                            <div key={sym} className="wafire-holding-card">
                              <div className="wafire-holding-title">
                                <span className="wafire-holding-symbol">{sym}</span>
                                <div className="d-flex flex-wrap gap-1 justify-content-end">
                                  <span className="badge bg-secondary-subtle text-secondary fw-bold">
                                    {diffShares >= 0 ? `거치식 +${diffShares.toLocaleString()}주` : `적립식 +${Math.abs(diffShares).toLocaleString()}주`}
                                  </span>
                                  {diffDiv !== 0 && (
                                    <span className={`badge ${diffDiv >= 0 ? "bg-primary-subtle text-primary" : "bg-success-subtle text-success"} fw-bold`}>
                                      {diffDiv >= 0 ? "거치배당 +" : "적립배당 +"}{formatKoreanAmount(Math.abs(diffDiv))}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="wafire-holding-row">
                                <span>거치식 보유 수량</span>
                                <span className="text-primary fw-bold">{lumpH.shares?.toLocaleString() || 0} 주</span>
                              </div>
                              <div className="wafire-holding-row">
                                <span>적립식 보유 수량</span>
                                <span className="text-success fw-bold">{dcaH.shares?.toLocaleString() || 0} 주</span>
                              </div>
                              <div className="wafire-holding-row">
                                <span>거치식 투입 원금</span>
                                <span>{formatKoreanAmount(lumpH.totalInvestedKRW || lumpH.investedKRW)}</span>
                              </div>
                              <div className="wafire-holding-row">
                                <span>적립식 투입 원금</span>
                                <span>{formatKoreanAmount(dcaH.totalInvestedKRW || dcaH.investedKRW)}</span>
                              </div>
                              <div className="wafire-holding-row">
                                <span>거치식 누적 배당금</span>
                                <span className="text-primary fw-bold">
                                  {formatKoreanAmount(lumpH.totalDividendKRW || 0)}
                                  {lumpH.totalDividendUSD ? ` ($${formatUSD(lumpH.totalDividendUSD)})` : ""}
                                </span>
                              </div>
                              <div className="wafire-holding-row">
                                <span>적립식 누적 배당금</span>
                                <span className="text-success fw-bold">
                                  {formatKoreanAmount(dcaH.totalDividendKRW || 0)}
                                  {dcaH.totalDividendUSD ? ` ($${formatUSD(dcaH.totalDividendUSD)})` : ""}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* =============================================================== */}
            {/* B. 단독 모드 또는 맞대결 상세 서브탭 테이블/보유 종목 */}
            {/* =============================================================== */}
            {(investmentType !== "COMPARE" || compareSubTab !== "summary") && activeSingleData && activeSingleData.summary && (
              <div>
                {/* 단독 모드일 때의 핵심 요약 4대 카드 */}
                {investmentType !== "COMPARE" && (
                  <div className="wafire-kpi-grid">
                    <div className="wafire-kpi-card highlight">
                      <div className="wafire-kpi-label">🔥 월평균 실수령 배당금</div>
                      <div className="wafire-kpi-val highlight-val">
                        <span className="wafire-kpi-prefix">매월</span>
                        <span className="wafire-kpi-num">{formatKRW(activeSingleData.summary.avgMonthlyNetKRW)}</span>
                        <span className="wafire-kpi-unit">원 겟!</span>
                      </div>
                      <div className="wafire-kpi-sub">
                        {(() => {
                          const mCount = Object.keys(activeSingleData.monthlyHistory || {}).length || 1;
                          const avgG = Math.round((activeSingleData.summary.totalGrossDivKRW || 0) / mCount);
                          return `세전 월 ${formatKRW(avgG)}원 중 세금·건보료 공제 (${mCount}개월 평균)`;
                        })()}
                      </div>
                      <div className="wafire-frog-verdict">
                        {getFrogVerdict(activeSingleData.summary.avgMonthlyNetKRW).title}
                      </div>
                    </div>

                    <div className="wafire-kpi-card">
                      <div className="wafire-kpi-label">📈 원금 방어율 & 주가 수익률</div>
                      <div
                        className="wafire-kpi-val"
                        style={{
                          color: (activeSingleData.summary.capitalReturnRate || 0) >= 0 ? "#10b981" : "#ef4444"
                        }}
                      >
                        {(activeSingleData.summary.capitalReturnRate || 0) >= 0 ? "+" : ""}
                        {activeSingleData.summary.capitalReturnRate}%
                      </div>
                      <div className="wafire-kpi-sub">
                        원금 {formatKoreanAmount(activeSingleData.summary.totalInvestedKRW)} ➔{" "}
                        <strong>{formatKoreanAmount(activeSingleData.summary.currentEvaluationKRW)}</strong>
                      </div>
                    </div>

                    <div className="wafire-kpi-card">
                      <div className="wafire-kpi-label">💰 누적 총 배당금 비교</div>
                      <div className="wafire-kpi-val" style={{ color: "#4f46e5" }}>
                        {formatKoreanAmount(activeSingleData.summary.totalNetDivKRW)}
                      </div>
                      <div className="wafire-kpi-sub">
                        세전 {formatKoreanAmount(activeSingleData.summary.totalGrossDivKRW)} 중{" "}
                        <span className="text-danger fw-bold">
                          {formatKoreanAmount(
                            activeSingleData.summary.totalGrossDivKRW - activeSingleData.summary.totalNetDivKRW
                          )}
                        </span>{" "}
                        공제됨
                      </div>
                    </div>

                    <div className="wafire-kpi-card">
                      <div className="wafire-kpi-label">🛡️ 세금/건보료 실효 공제율</div>
                      <div className="wafire-kpi-val" style={{ color: "#d97706" }}>
                        {activeSingleData.summary.totalGrossDivKRW > 0
                          ? (
                              ((activeSingleData.summary.totalGrossDivKRW - activeSingleData.summary.totalNetDivKRW) /
                                activeSingleData.summary.totalGrossDivKRW) *
                              100
                            ).toFixed(1)
                          : 0}
                        %
                      </div>
                      <div className="wafire-kpi-sub">
                        미국세 15% + 금융소득종합과세 + 건보료 합산
                      </div>
                    </div>
                  </div>
                )}

                {/* 단독 모드 차트 */}
                {investmentType !== "COMPARE" && (
                  <Row className="mb-4">
                    <Col lg={7} className="mb-4 mb-lg-0">
                      <div className="wafire-card h-100">
                        <div className="wafire-card-title d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <span className="d-flex align-items-center gap-2">
                            <span className="wafire-card-title-icon"><FaChartLine /></span>
                            월별 배당금 입금 추이 ({chartTaxMode === "net" ? "세후 실수령" : "세전"}, ₩ / $)
                          </span>
                          <div className="wafire-tax-toggle">
                            <button
                              type="button"
                              className={`wafire-tax-btn ${chartTaxMode === "net" ? "active net" : ""}`}
                              onClick={() => setChartTaxMode("net")}
                            >
                              💧 세후 실수령
                            </button>
                            <button
                              type="button"
                              className={`wafire-tax-btn ${chartTaxMode === "gross" ? "active" : ""}`}
                              onClick={() => setChartTaxMode("gross")}
                            >
                              📦 세전
                            </button>
                          </div>
                        </div>
                        <div className="wafire-chart-wrapper">
                          {singleMonthlyChartData && (
                            <Bar
                              data={singleMonthlyChartData}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                interaction: {
                                  mode: "index",
                                  intersect: false
                                },
                                plugins: {
                                  legend: {
                                    display: Boolean(singleMonthlyChartData.isStacked),
                                    position: "top",
                                    labels: { font: { size: 11, weight: "bold" }, boxWidth: 14 }
                                  },
                                  tooltip: {
                                    callbacks: {
                                      title: (items) => {
                                        const m = items[0]?.label || "";
                                        return chartTaxMode === "net" ? `${m} 배당금 (실수령)` : `${m} 배당금 (세전)`;
                                      },
                                      label: (ctx) => {
                                        const month = ctx.label;
                                        const item = activeSingleData?.monthlyHistory?.[month];
                                        const year = month.split("-")[0];
                                        const yData = activeSingleData?.yearlySummary?.[year];
                                        const netRatio = yData?.grossKRW > 0 ? yData.netKRW / yData.grossKRW : 0.85;

                                        const sym = ctx.dataset.label.replace(" 배당금", "").replace(" 실수령", "").replace(" 월", "").replace(" 세전", "");
                                        const symData = item?.bySymbol?.[sym];
                                        const grossKRW = Math.round(symData?.grossKRW ?? item?.grossKRW ?? ctx.raw ?? 0);
                                        const netKRW = Math.round(grossKRW * netRatio);

                                        if (chartTaxMode === "net") {
                                          const prefix = singleMonthlyChartData.isStacked ? `• ${sym}` : "월 실수령액";
                                          return ` ${prefix}: ${netKRW.toLocaleString()}원 (세전 ${grossKRW.toLocaleString()}원)`;
                                        } else {
                                          const grossUSD = symData?.grossUSD ?? item?.grossUSD;
                                          const u = grossUSD != null ? formatUSD(grossUSD) : null;
                                          const prefix = singleMonthlyChartData.isStacked ? `• ${sym}` : "월 세전 배당금";
                                          return u
                                            ? ` ${prefix}: ${grossKRW.toLocaleString()}원 ($${u})`
                                            : ` ${prefix}: ${grossKRW.toLocaleString()}원`;
                                        }
                                      },
                                      footer: (items) => {
                                        const month = items[0]?.label;
                                        const item = activeSingleData?.monthlyHistory?.[month];
                                        if (!item) return "";
                                        const year = month.split("-")[0];
                                        const yData = activeSingleData?.yearlySummary?.[year];
                                        const netRatio = yData?.grossKRW > 0 ? yData.netKRW / yData.grossKRW : 0.85;

                                        const grossKRW = Math.round(item.grossKRW || 0);
                                        const netKRW = Math.round(grossKRW * netRatio);
                                        const dedKRW = grossKRW - netKRW;
                                        const dedRate = grossKRW > 0 ? (((grossKRW - netKRW) / grossKRW) * 100).toFixed(1) : "0.0";
                                        const grossUSD = item.grossUSD != null ? formatUSD(item.grossUSD) : null;
                                        const netUSD = item.grossUSD != null ? formatUSD(item.grossUSD * netRatio) : null;

                                        return [
                                          "──────────────────────────────",
                                          `🔥 통장 실수령: ${netKRW.toLocaleString()}원${netUSD ? ` ($${netUSD})` : ""}`,
                                          `💸 세금·건보 공제: -${dedKRW.toLocaleString()}원 (${dedRate}%)`,
                                          `📦 세전 총 배당: ${grossKRW.toLocaleString()}원${grossUSD ? ` ($${grossUSD})` : ""}`
                                        ].join("\n");
                                      }
                                    }
                                  }
                                },
                                scales: {
                                  x: {
                                    stacked: Boolean(singleMonthlyChartData.isStacked),
                                    grid: { display: false },
                                    ticks: { font: { size: 10 }, maxRotation: 45 }
                                  },
                                  y: {
                                    stacked: Boolean(singleMonthlyChartData.isStacked),
                                    grid: { color: "rgba(226, 232, 240, 0.6)" },
                                    ticks: { callback: (val) => `${Math.round(val / 10000)}만` }
                                  }
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col lg={5}>
                      <div className="wafire-card h-100">
                        <div className="wafire-card-title">
                          <span className="d-flex align-items-center gap-2">
                            <span className="wafire-card-title-icon"><FaFileInvoiceDollar /></span>
                            연도별 실수령 vs 세금/건보료
                          </span>
                        </div>
                        <div className="wafire-chart-wrapper">
                          {singleYearlyChartData && (
                            <Bar
                              data={singleYearlyChartData}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                  legend: { position: "top", labels: { font: { size: 11, weight: "bold" } } },
                                  tooltip: {
                                    callbacks: {
                                      label: (ctx) => ` ${ctx.dataset.label}: ${Math.round(ctx.raw || 0).toLocaleString()}원`
                                    }
                                  }
                                },
                                scales: {
                                  x: { stacked: true, grid: { display: false } },
                                  y: {
                                    stacked: true,
                                    grid: { color: "rgba(226, 232, 240, 0.6)" },
                                    ticks: { callback: (val) => `${Math.round(val / 10000)}만` }
                                  }
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}

                {/* 연도별 상세 테이블 */}
                <div className="wafire-card mb-4">
                  <div className="wafire-card-title">
                    <span className="d-flex align-items-center gap-2">
                      <span className="wafire-card-title-icon"><FaMoneyBillWave /></span>
                      {investmentType === "COMPARE"
                        ? compareSubTab === "dca"
                          ? "💧 적립식 연도별 배당금 및 공제 상세 내역"
                          : "📦 거치식 연도별 배당금 및 공제 상세 내역"
                        : investmentType === "DCA"
                          ? "💧 적립식(월 분할 적립) 연도별 배당금 및 공제 상세 내역"
                          : "📦 거치식(일시 투자) 연도별 배당금 및 공제 상세 내역"}
                    </span>
                    <span className="small text-muted">단위: 원화(₩)</span>
                  </div>

                  <div className="wafire-table-responsive">
                    <table className="wafire-table">
                      <thead>
                        <tr>
                          <th>연도</th>
                          <th>세전 총 배당금</th>
                          <th>미국 원천징수 (15%)</th>
                          <th>국내 종합소득세</th>
                          <th>건강보험료 타격</th>
                          <th>최종 실수령액</th>
                          <th>실수령 비율</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeSingleData.yearlySummary &&
                          Object.keys(activeSingleData.yearlySummary)
                            .sort()
                            .map((year) => {
                              const item = activeSingleData.yearlySummary[year];
                              const netRatio = item.grossKRW > 0 ? ((item.netKRW / item.grossKRW) * 100).toFixed(1) : "0.0";
                              return (
                                <tr key={year}>
                                  <td>{year}년</td>
                                  <td>{formatKRW(item.grossKRW)} 원</td>
                                  <td className="text-secondary">-{formatKRW(item.usTaxKRW)} 원</td>
                                  <td className="text-warning">
                                    {item.extraTaxKRW > 0 ? `-${formatKRW(item.extraTaxKRW)} 원` : "0 원"}
                                  </td>
                                  <td className="text-danger">
                                    {item.healthInsKRW > 0 ? `-${formatKRW(item.healthInsKRW)} 원` : "0 원"}
                                  </td>
                                  <td className="fw-bold text-success">
                                    {formatKRW(item.netKRW)} 원
                                  </td>
                                  <td>
                                    <span className="badge bg-success-subtle text-success fw-bold">
                                      {netRatio}%
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}

                        {activeYearlyTotals && (
                          <tr className="total-row">
                            <td>총 합계</td>
                            <td>{formatKRW(activeYearlyTotals.grossKRW)} 원</td>
                            <td className="text-secondary">-{formatKRW(activeYearlyTotals.usTaxKRW)} 원</td>
                            <td className="text-warning">-{formatKRW(activeYearlyTotals.extraTaxKRW)} 원</td>
                            <td className="text-danger">-{formatKRW(activeYearlyTotals.healthInsKRW)} 원</td>
                            <td className="fw-bold text-success fs-6">
                              {formatKRW(activeYearlyTotals.netKRW)} 원
                            </td>
                            <td>
                              <span className="badge bg-primary text-white fw-bold">
                                {activeYearlyTotals.grossKRW > 0
                                  ? ((activeYearlyTotals.netKRW / activeYearlyTotals.grossKRW) * 100).toFixed(1)
                                  : "0.0"}
                                %
                              </span>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 📅 월별 & ETF 종목별 배당금 입금 상세 내역 테이블 */}
                {activeSingleData.monthlyHistory && Object.keys(activeSingleData.monthlyHistory).length > 0 && (
                  <div className="wafire-card mb-4">
                    <div className="wafire-card-title d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <span className="d-flex align-items-center gap-2">
                        <span className="wafire-card-title-icon"><FaCalendarAlt /></span>
                        {investmentType === "COMPARE"
                          ? compareSubTab === "dca"
                            ? `💧 적립식 월별 & ETF 종목별 배당금 입금 내역 (${chartTaxMode === "net" ? "세후 실수령" : "세전"})`
                            : `📦 거치식 월별 & ETF 종목별 배당금 입금 내역 (${chartTaxMode === "net" ? "세후 실수령" : "세전"})`
                          : investmentType === "DCA"
                            ? `💧 적립식 월별 & ETF 종목별 배당금 입금 내역 (${chartTaxMode === "net" ? "세후 실수령" : "세전"})`
                            : `📦 거치식 월별 & ETF 종목별 배당금 입금 내역 (${chartTaxMode === "net" ? "세후 실수령" : "세전"})`}
                      </span>
                      <div className="d-flex align-items-center gap-3">
                        <span className="small text-muted">단위: 원화(₩) / 달러($)</span>
                        <div className="wafire-tax-toggle">
                          <button
                            type="button"
                            className={`wafire-tax-btn ${chartTaxMode === "net" ? "active net" : ""}`}
                            onClick={() => setChartTaxMode("net")}
                          >
                            💧 세후 실수령
                          </button>
                          <button
                            type="button"
                            className={`wafire-tax-btn ${chartTaxMode === "gross" ? "active" : ""}`}
                            onClick={() => setChartTaxMode("gross")}
                          >
                            📦 세전
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="wafire-table-responsive wafire-table-scroll">
                      <table className="wafire-table">
                        <thead>
                          <tr>
                            <th>연월</th>
                            <th>{chartTaxMode === "net" ? "월 총 배당금 (실수령)" : "월 총 배당금 (세전)"}</th>
                            {(Object.keys(activeSingleData.holdings || {}).length > 0
                              ? Object.keys(activeSingleData.holdings)
                              : allocations.map((a) => a.symbol)
                            ).map((sym) => (
                              <th key={sym}>{sym} {chartTaxMode === "net" ? "실수령" : "배당금"}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {Object.keys(activeSingleData.monthlyHistory)
                            .sort()
                            .map((month) => {
                              const item = activeSingleData.monthlyHistory[month] || {};
                              const year = month.split("-")[0];
                              const yData = activeSingleData?.yearlySummary?.[year];
                              const netRatio = yData?.grossKRW > 0 ? yData.netKRW / yData.grossKRW : 0.85;

                              const symbols = Object.keys(activeSingleData.holdings || {}).length > 0
                                ? Object.keys(activeSingleData.holdings)
                                : allocations.map((a) => a.symbol);
                              const grossKRW = Math.round(item.grossKRW || 0);
                              const netKRW = Math.round(grossKRW * netRatio);
                              const krw = chartTaxMode === "net" ? netKRW : grossKRW;
                              const usd = item.grossUSD != null
                                ? formatUSD(chartTaxMode === "net" ? item.grossUSD * netRatio : item.grossUSD)
                                : null;

                              return (
                                <tr key={month}>
                                  <td>{month}</td>
                                  <td className="fw-bold">
                                    <span className="text-dark">{krw.toLocaleString()}원</span>
                                    {usd && <span className="text-muted small ms-1">(${usd})</span>}
                                  </td>
                                  {symbols.map((sym) => {
                                    const symData = item.bySymbol?.[sym];
                                    const sGross = Math.round(symData?.grossKRW || 0);
                                    const sNet = Math.round(sGross * netRatio);
                                    const sKrw = chartTaxMode === "net" ? sNet : sGross;
                                    const sUsd = symData?.grossUSD != null
                                      ? formatUSD(chartTaxMode === "net" ? symData.grossUSD * netRatio : symData.grossUSD)
                                      : null;

                                    return (
                                      <td key={sym}>
                                        {sKrw > 0 ? (
                                          <div>
                                            <span className="text-primary fw-semibold">{sKrw.toLocaleString()}원</span>
                                            {sUsd && <span className="text-muted small ms-1">(${sUsd})</span>}
                                          </div>
                                        ) : (
                                          <span className="text-muted">-</span>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}

                          {/* 총 누적 배당금 합계 행 */}
                          <tr className="total-row">
                            <td>{chartTaxMode === "net" ? "총 누적 실수령액" : "총 누적 세전 배당금"}</td>
                            <td className="fw-bold text-success fs-6">
                              {chartTaxMode === "net"
                                ? `${formatKRW(activeSingleData.summary?.totalNetDivKRW)}원`
                                : `${formatKRW(activeSingleData.summary?.totalGrossDivKRW)}원`}
                              {activeSingleData.summary?.totalGrossDivUSD != null && (
                                <span className="small opacity-75 ms-1">
                                  (${formatUSD(
                                    chartTaxMode === "net"
                                      ? (activeSingleData.summary.totalGrossDivUSD * (activeSingleData.summary.totalGrossDivKRW > 0 ? (activeSingleData.summary.totalNetDivKRW / activeSingleData.summary.totalGrossDivKRW) : 0.85))
                                      : activeSingleData.summary.totalGrossDivUSD
                                  )})
                                </span>
                              )}
                            </td>
                            {(Object.keys(activeSingleData.holdings || {}).length > 0
                              ? Object.keys(activeSingleData.holdings)
                              : allocations.map((a) => a.symbol)
                            ).map((sym) => {
                              const h = activeSingleData.holdings?.[sym];
                              const grossRatio = (activeSingleData.summary?.totalGrossDivKRW > 0)
                                ? (activeSingleData.summary.totalNetDivKRW / activeSingleData.summary.totalGrossDivKRW)
                                : 0.85;
                              const tGross = h?.totalDividendKRW || 0;
                              const tNet = Math.round(tGross * grossRatio);
                              const tKrw = chartTaxMode === "net" ? tNet : tGross;
                              const tUsd = h?.totalDividendUSD != null
                                ? formatUSD(chartTaxMode === "net" ? h.totalDividendUSD * grossRatio : h.totalDividendUSD)
                                : null;

                              return (
                                <td key={sym} className="fw-bold text-primary">
                                  {tKrw > 0 ? (
                                    <div>
                                      <span>{formatKRW(tKrw)}원</span>
                                      {tUsd != null && (
                                        <span className="small text-muted ms-1">
                                          (${tUsd})
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ETF 보유 종목별 매수단가, 수익률 & 누적 배당금 현황 */}
                {activeSingleData.holdings && (
                  <div className="wafire-card">
                    <div className="wafire-card-title">
                      <span className="d-flex align-items-center gap-2">
                        <span className="wafire-card-title-icon"><FaExchangeAlt /></span>
                        {investmentType === "COMPARE"
                          ? compareSubTab === "dca"
                            ? "💧 적립식 ETF 보유 종목별 현황 & 누적 배당금"
                            : "📦 거치식 ETF 보유 종목별 현황 & 누적 배당금"
                          : investmentType === "DCA"
                            ? "💧 적립식 ETF 보유 종목별 현황 & 누적 배당금"
                            : "📦 거치식 ETF 보유 종목별 현황 & 누적 배당금"}
                      </span>
                    </div>

                    <div className="wafire-holdings-grid">
                      {Object.keys(activeSingleData.holdings).map((sym) => {
                        const h = activeSingleData.holdings[sym];
                        const invested = h.investedKRW || h.totalInvestedKRW || 0;
                        const returnRate =
                          h.buyPriceUSD > 0 && h.currentPriceUSD
                            ? (((h.currentPriceUSD - h.buyPriceUSD) / h.buyPriceUSD) * 100).toFixed(2)
                            : null;
                        const divYieldOnCost =
                          invested > 0 && h.totalDividendKRW > 0
                            ? (((h.totalDividendKRW || 0) / invested) * 100).toFixed(2)
                            : null;

                        return (
                          <div key={sym} className="wafire-holding-card">
                            <div className="wafire-holding-title">
                              <span className="wafire-holding-symbol">{sym}</span>
                              <div className="d-flex flex-wrap gap-1 justify-content-end">
                                {returnRate !== null && (
                                  <span
                                    className={`badge ${Number(returnRate) >= 0 ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}
                                  >
                                    주가 {Number(returnRate) >= 0 ? "+" : ""}{returnRate}%
                                  </span>
                                )}
                                {divYieldOnCost !== null && (
                                  <span className="badge bg-primary-subtle text-primary">
                                    배당수익률 +{divYieldOnCost}%
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="wafire-holding-row">
                              <span>보유 수량</span>
                              <span className="fw-bold">{h.shares?.toLocaleString()} 주</span>
                            </div>
                            {h.buyPriceUSD && (
                              <div className="wafire-holding-row">
                                <span>매수 단가</span>
                                <span>${formatUSD(h.buyPriceUSD)}</span>
                              </div>
                            )}
                            {h.currentPriceUSD && (
                              <div className="wafire-holding-row">
                                <span>현재가</span>
                                <span>${formatUSD(h.currentPriceUSD)}</span>
                              </div>
                            )}
                            <div className="wafire-holding-row">
                              <span>투자 원금</span>
                              <span>{formatKoreanAmount(invested)}</span>
                            </div>
                            <div className="wafire-holding-row">
                              <span>누적 배당금</span>
                              <span className="text-primary fw-bold">
                                {formatKoreanAmount(h.totalDividendKRW || 0)}
                                {h.totalDividendUSD ? ` ($${formatUSD(h.totalDividendUSD)})` : ""}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </Container>
    </div>
  );
};

export default WaFireSimulator;
